import { CameraControls } from "@react-three/drei";
import { useThree } from "@react-three/fiber";
import { useEffect, useRef } from "react";

import * as THREE from "three";

type CameraSetup = {
  position: THREE.Vector3;
  target: THREE.Vector3;
  rotation: THREE.Euler;
  near: number;
  far: number;
  fov?: number;
  movementSpeed: number;
  panSpeed: number;
  orbitMinDistance: number;
  orbitMaxDistance: number;
  orbitInitialDistance: number;
};

export const cameraSetups: { [key: string]: CameraSetup } = {
  join: {
    position: new THREE.Vector3(
      4.962727392066462,
      9.065193636090791,
      2.3868554101691,
    ),
    target: new THREE.Vector3(
      -5.654397734784007,
      -1.4340526117696366,
      1.9788071564226,
    ),
    rotation: new THREE.Euler(-2.1, 0.65, 2.3),
    near: 0.005,
    far: 1000,
    fov: 50,
    movementSpeed: 1,
    panSpeed: 10,
    orbitMinDistance: 5,
    orbitMaxDistance: 20,
    orbitInitialDistance: 10,
  },
  under: {
    position: new THREE.Vector3(-13.8, -12, -7),
    target: new THREE.Vector3(-6.7, -3, 2),
    rotation: new THREE.Euler(-2.1, 0.65, 2.3),
    near: 0.005,
    far: 1000,
    fov: 50,
    movementSpeed: 0.3,
    panSpeed: 10,
    orbitMinDistance: 5,
    orbitMaxDistance: 20,
    orbitInitialDistance: 10,
  },
  isolators: {
    position: new THREE.Vector3(-5.8, 0.8, -5.2),
    target: new THREE.Vector3(-3.2, 3.9, 2.9),
    rotation: new THREE.Euler(2.77, -0.29, 3),
    near: 0.005,
    far: 1000,
    fov: 50,
    movementSpeed: 0.3,
    panSpeed: 10,
    orbitMinDistance: 5,
    orbitMaxDistance: 20,
    orbitInitialDistance: 10,
  },
  system: {
    position: new THREE.Vector3(-4, 15, -0.7),
    target: new THREE.Vector3(-4, -12, -0.7),
    rotation: new THREE.Euler(-1.57, 1.8, 3.1),
    near: 0.005,
    far: 1000,
    movementSpeed: 0.5,
    panSpeed: 1,
    orbitMinDistance: 5,
    orbitMaxDistance: 20,
    orbitInitialDistance: 10,
  },
  // TODO
  insideChip: {
    position: new THREE.Vector3(
      -10.160171384332944,
      1.697251960217636,
      25.268130561985505,
    ),
    target: new THREE.Vector3(
      -10.067715095637997,
      1.6026099186952962,
      25.350706826627146,
    ),
    rotation: new THREE.Euler(-2.47, -0.385, -2.852),
    near: 0.005,
    far: 1000,
    fov: 50,
    movementSpeed: 0.05,
    panSpeed: 0.005,
    orbitMinDistance: 0.1,
    orbitMaxDistance: 2,
    orbitInitialDistance: 1,
  },

  fuseBreak: {
    position: new THREE.Vector3(
      -10.135054133778258,
      1.6946882710668898,
      25.287068002904252,
    ),
    target: new THREE.Vector3(
      -10.132668449395764,
      1.6795733603911263,
      25.291323700949654,
    ),
    rotation: new THREE.Euler(-2.47, -0.385, -2.852),
    near: 0.005,
    far: 1000,
    fov: 50,
    movementSpeed: 0.05,
    panSpeed: 0.005,
    orbitMinDistance: 0.1,
    orbitMaxDistance: 2,
    orbitInitialDistance: 1,
  },
};

function CameraController({
  currentCamera,
  cameraControls,
}: {
  currentCamera: string;
  cameraControls: React.RefObject<CameraControls | undefined>;
}) {
  const { camera }: { camera: THREE.PerspectiveCamera } = useThree();

  // Track whether this is the first time we load.
  const initialLoadRef = useRef(true);

  useEffect(() => {
    const setup = cameraSetups[currentCamera];
    if (!setup) return;

    // If it's the first load, disable transitions (false).
    // Otherwise, do a smooth transition (true).
    cameraControls?.current?.setLookAt(
      setup.position.x,
      setup.position.y,
      setup.position.z,
      setup.target.x,
      setup.target.y,
      setup.target.z,
      !initialLoadRef.current,
    );

    camera.near = setup.near;
    camera.far = setup.far;
    camera.fov = setup.fov || 50;
    camera.updateProjectionMatrix();

    // Once we've set up the camera the first time, mark this as not initial anymore.
    initialLoadRef.current = false;
  }, [currentCamera, cameraControls, camera]);

  return null;
}

export default CameraController;
